import { useEffect, useMemo, useState } from 'react'

/**
 * The "Report look" panel — one definition, every tool that renders anatomy.
 *
 * These controls lived inside PreviewPage (STL/OBJ to PDF), so the Case Report
 * Builder — which renders the SAME meshes through the SAME renderer — had no
 * controls at all: it took whatever REPORT_LOOK said and the operator could not
 * touch it. The team's decision is that one set of controls governs anatomy
 * images wherever they are produced, so the panel moved here and both tools
 * mount it.
 *
 * WHAT THIS FILE IS NOT. It is not the house look. `REPORT_LOOK` in
 * meshRenderer.js remains the definition of how a report renders — the matcap,
 * the bone tone, the GTAO constants, the key light — and it is pinned to
 * shared/report-look.json and the Surgical Suite Engine's copy by
 * sidecar/tests/test_report_look_parity.py. Callers still spread REPORT_LOOK
 * FIRST and apply `renderOpts` from this hook on top. Nothing here may change
 * those constants; doing so silently splits the portal's renders from SSE's,
 * which is the #C9BD9C vs #685F58 failure that spec was written to end.
 *
 * Usage:
 *   const rl = useReportLook()
 *   <ReportLookPanel value={rl} />
 *   renderReport(bytes, { ...REPORT_LOOK, ...rl.renderOpts, colors, filename })
 */

// ZBrush "matcap skeleton" lit-sphere texture → MeshMatcapMaterial (exact ZBrush look).
export const MATCAP_URL = '/matcaps/skeleton.png'

// SSE Report-look presets (ReportSetupModal.jsx).
export const BONE_TONES = [['Bone', '#CFC1A7'], ['Ivory', '#E9DBC0'], ['Light', '#F4EBD4']]
export const PAGE1_TITLES = ['Images for Confirmation of Fabrication', 'Images for Review']

/**
 * Where the two sliders open, and how far they travel.
 *
 * BOTH ARE THE ORIGINAL VALUES and must stay that way without a measurement
 * saying otherwise. The defaults equal REPORT_LOOK, and the ranges are tight on
 * purpose: they bracket the working band so most of the travel is usable.
 *
 * ⚠️ These were changed twice and both changes were wrong. First the defaults
 * moved to 0.65 / 2.0 on an invented premise ("the team works at 0.65"); measured
 * afterwards, the brightness default alone shifted every untouched render by
 * 17.37/255 and the team asked for the earlier look back. Then the ranges were
 * left wide (0-2.2 and 0-4) on the reasoning that a wider slider is free. It is
 * not: widening the shadow slider from 0.3-2.0 to 0-4 dropped the useful band
 * from ~59% of the track to ~25%, so a drag lands on a bad value far more easily.
 * A preview PDF came back at matcap 2.0 / shadows 0.65 — blown highlights, no
 * crevice depth, sutures gone.
 *
 * The Surgical Suite Engine renders the same look correctly with the SAME code —
 * MeshMatcapMaterial at color 0xffffff, LinearToneMapping at exposure =
 * matcapBrightness, the same GTAO constants — and it has NO sliders for these. It
 * cannot wander. That is the whole difference, and it is why these ranges are
 * narrow rather than generous.
 */
export const MATCAP_BRIGHTNESS = { min: 0.25, max: 2.0, step: 0.05, default: 1.1 }
export const AO_INTENSITY = { min: 0.3, max: 2.0, step: 0.05, default: 1.3 }

/**
 * Stamp relief — the self-shadow pass that makes an engraved case ID read face-on.
 *
 * 0 IS OFF, which is the default and is what matches the Surgical Suite Engine: SSE
 * has no relief code at all, so while this ran unconditionally the two apps could not
 * produce the same image however the other sliders were set. Measured on the delivered
 * case, on-at-0.55 versus off is mean 1.90/255, worst pixel 117, 8.0% of pixels — the
 * "too much shadow" the team reported.
 *
 * One slider rather than a checkbox plus a strength: zero already means off, and a
 * second control to reason about is how this panel got churned in the first place.
 */
export const STAMP_RELIEF = { min: 0, max: 1, step: 0.05, default: 0 }

/**
 * What the slider jumps to when the operator MARKS a stamp group.
 *
 * Marking a group is the declaration of intent, so one action has to be enough — the team
 * should not have to mark the stamp, see it render faint, and then know to reach for a
 * second control. Nothing else arms it: with no group marked the slider stays at
 * STAMP_RELIEF.default (0) and the render is byte-identical to the Surgical Suite Engine
 * match, so this cannot leak into a file that did not ask for it.
 *
 * The brief was the LOWEST strength that reads at page scale, and this was chosen by printing
 * it rather than by reading the numbers. Measured on the reference case's Posterior view
 * (mean 5x5 local range inside the stamp box; flat unengraved bone is 8.92):
 *
 *   off    16.82   what the team called faded
 *   0.55   20.45   better, but only a modest step off faded at 300 dpi
 *   0.75   ~22.0   reads cleanly at print scale  <-- here
 *   1.00   ~25.4   stronger still, and the headroom is left for the odd shallow engraving
 *
 * The tone match is NOT what bounds this choice, which is worth stating because it is the
 * thing that would normally bound it: the lit plate holds at p95 219-220 across the entire
 * sweep, so the stamp never starts reading as a patch stuck onto the skull. What bounds it
 * is taste, so it is set where the printed page reads and left adjustable.
 * See labelRigLights in meshRenderer.js for the sweep and for the two levers this replaced.
 */
export const STAMP_RELIEF_MARKED = 0.75

/**
 * Realistic-mode key light, in degrees relative to each view.
 *
 * NAMED, not three loose useState literals, for the same reason as the two above:
 * while they were inline the parity test could not see them, so `lightIntensity`
 * could be changed in the spec and in both REPORT_LOOK copies with every check
 * still green while the portal's Realistic renders drifted. Pinned now.
 */
export const LIGHT_DEFAULTS = { azimuth: -30, elevation: 45, intensity: 1.2 }

const LOOK_KEY = 'sse.reportLook'
const LOOK_FALLBACK = { boneColor: '#CFC1A7', shadingT: 0.5, roughness: 0.82, background: 'white', page1Title: PAGE1_TITLES[0] }

function loadLook() {
  try {
    const s = JSON.parse(localStorage.getItem(LOOK_KEY))
    if (s?.boneColor) {
      return {
        boneColor: s.boneColor,
        shadingT: s.shadingT ?? LOOK_FALLBACK.shadingT,
        roughness: s.roughness ?? LOOK_FALLBACK.roughness,
        background: s.background || LOOK_FALLBACK.background,
        page1Title: s.page1Title || LOOK_FALLBACK.page1Title,
      }
    }
  } catch { /* noop */ }
  return { ...LOOK_FALLBACK }
}

/**
 * All the look state, plus the render options derived from it.
 *
 * PERSISTENCE is deliberately partial and unchanged from what PreviewPage
 * already did: only boneColor / shadingT / roughness / background / page1Title
 * survive a reload. Matcap brightness, shadow strength, render style and the
 * light placement reset to the defaults above every time — which is the whole
 * point of setting those defaults. A persisted brightness would mean the panel
 * reopens wherever it was last dragged, and the house default would be true
 * exactly once per browser.
 *
 * The key is shared across tools, so a bone tone chosen in STL/OBJ to PDF is the
 * one the Case Report Builder opens with.
 */
export function useReportLook() {
  const [look, setLook] = useState(loadLook)
  useEffect(() => { try { localStorage.setItem(LOOK_KEY, JSON.stringify(look)) } catch { /* noop */ } }, [look])
  const setL = (k, v) => setLook((p) => ({ ...p, [k]: v }))

  // Default true: the team's OBJ exports are RAS (anterior=+Y); the renderer's default
  // frame is LPS (anterior=-Y), so flip so "Anterior" shows the face. Uncheck for LPS STLs.
  const [flipAP, setFlipAP] = useState(true)
  const [style, setStyle] = useState('matcap') // 'matcap' (ZBrush look) | 'realistic' (lit + real cast shadows)
  const matcap = style === 'matcap'
  const [lightAz, setLightAz] = useState(LIGHT_DEFAULTS.azimuth)
  const [lightEl, setLightEl] = useState(LIGHT_DEFAULTS.elevation)
  const [lightIntensity, setLightIntensity] = useState(LIGHT_DEFAULTS.intensity)
  const [matcapBrightness, setMatcapBrightness] = useState(MATCAP_BRIGHTNESS.default)
  const [shadows, setShadows] = useState(true) // GTAO ambient-occlusion crevice shadows (BPR look)
  const [aoIntensity, setAoIntensity] = useState(AO_INTENSITY.default)
  const [stampRelief, setStampRelief] = useState(STAMP_RELIEF.default)

  // What a caller merges OVER REPORT_LOOK. `page1Title` is deliberately absent —
  // it is a report field, not a render parameter, and passing it to renderReport
  // would be meaningless noise in the Builder's option set.
  const renderOpts = useMemo(() => ({
    background: look.background,
    boneColor: look.boneColor,
    shadingT: look.shadingT,
    roughness: look.roughness,
    flipAP,
    matcapUrl: matcap ? MATCAP_URL : null,
    matcapBrightness,
    shadows,
    aoIntensity,
    castShadows: !matcap,
    // 0 = off. The renderer takes a boolean and a strength; the panel keeps it to one
    // number so "off" and "how much" cannot disagree.
    reliefShadow: stampRelief > 0,
    reliefStrength: stampRelief,
    lightAzimuth: lightAz,
    lightElevation: lightEl,
    lightIntensity,
  }), [look, flipAP, matcap, matcapBrightness, shadows, aoIntensity, stampRelief, lightAz, lightEl, lightIntensity])

  return {
    look, setL, flipAP, setFlipAP, style, setStyle, matcap,
    lightAz, setLightAz, lightEl, setLightEl, lightIntensity, setLightIntensity,
    matcapBrightness, setMatcapBrightness, shadows, setShadows, aoIntensity, setAoIntensity,
    stampRelief, setStampRelief,
    renderOpts,
  }
}

const field = 'w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm'
const lbl = 'block text-xs font-medium text-gray-500 mb-1'

/**
 * @param {object}  props.value            the useReportLook() return value
 * @param {boolean} [props.showPage1Title] Page-1 header select. STL/OBJ to PDF only —
 *        PAGE1_TITLES names the header of that report's first page and has no
 *        counterpart in a case report, whose page titles come from its template.
 * @param {string}  [props.title]          panel heading
 * @param {boolean} [props.showToneControls]  bone tone / shading / finish.
 *   The Case Report Builder passes false, and that is a CORRECTNESS matter, not
 *   tidiness. A case report's parts carry their own colours from `colorForRole`,
 *   and the renderer decides whether a part colour is a deliberate tint by
 *   comparing it against `boneColor` (meshRenderer.js). Move `boneColor` off the
 *   palette's BONE and every anatomy part suddenly reads as tinted: its ZBrush
 *   polypaint is discarded and the matcap is multiplied by #CFC1A7, the ~18%
 *   olive-taupe darkening REPORT_RENDER_OPTS exists to prevent. `boneColor`
 *   persists while the style toggle does not, so it would come back silently.
 * @param {boolean} [props.showBackground]  white/black ground. Also false in the
 *   Builder: a case report is printed on white A4 and a black ground is never
 *   right there, but the key is shared, so Black chosen in STL/OBJ to PDF would
 *   otherwise follow the operator into every case report they build.
 */
export default function ReportLookPanel({
  value, showPage1Title = true, showToneControls = true, showBackground = true,
  title = 'Report look',
}) {
  const {
    look, setL, flipAP, setFlipAP, style, setStyle, matcap,
    lightAz, setLightAz, lightEl, setLightEl, lightIntensity, setLightIntensity,
    matcapBrightness, setMatcapBrightness, shadows, setShadows, aoIntensity, setAoIntensity,
    stampRelief, setStampRelief,
  } = value

  return (
    <div className="card space-y-3">
      <span className="text-sm font-semibold text-gray-700 block">{title}</span>
      {/* Tone/Shading/Finish only apply to the lit fallback — disabled while matcap is on. */}
      {showToneControls && (
      <div className={matcap ? 'space-y-3 opacity-40 pointer-events-none' : 'space-y-3'}>
        <div>
          <label className={lbl}>Bone tone</label>
          <div className="flex items-center gap-2">
            {BONE_TONES.map(([label, hex]) => (
              <button key={hex} title={label} disabled={matcap} onClick={() => setL('boneColor', hex)}
                className={`w-8 h-8 rounded border ${look.boneColor === hex ? 'border-brand-500 ring-1 ring-brand-500' : 'border-gray-300'}`} style={{ backgroundColor: hex }} />
            ))}
          </div>
        </div>
        <div>
          <div className="flex justify-between text-xs text-gray-500 mb-0.5"><span>Shading</span><span>soft ← → hard</span></div>
          <input type="range" min={0} max={1} step={0.05} disabled={matcap} value={look.shadingT} onChange={(e) => setL('shadingT', parseFloat(e.target.value))} className="w-full accent-brand-500" />
        </div>
        <div>
          <div className="flex justify-between text-xs text-gray-500 mb-0.5"><span>Finish</span><span>glossy ← → matte</span></div>
          <input type="range" min={0.3} max={1.0} step={0.02} disabled={matcap} value={look.roughness} onChange={(e) => setL('roughness', parseFloat(e.target.value))} className="w-full accent-brand-500" />
        </div>
      </div>
      )}
      {showBackground && (
      <div>
        <label className={lbl}>Background</label>
        <div className="flex gap-2 text-sm">
          {[['White', 'white'], ['Black', 'black']].map(([label, val]) => (
            <button key={val} onClick={() => setL('background', val)}
              className={`px-3 py-1 rounded ${look.background === val ? 'btn-primary' : 'btn-secondary'}`}>{label}</button>
          ))}
        </div>
      </div>
      )}
      {showPage1Title && (
        <div>
          <label className={lbl}>Page-1 header</label>
          <select value={look.page1Title} onChange={(e) => setL('page1Title', e.target.value)} className={field}>
            {PAGE1_TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      )}
      <div className="border-t border-gray-100 pt-2">
        <label className={lbl}>Render style</label>
        <div className="flex gap-2 text-sm">
          {[['Matcap', 'matcap'], ['Realistic (shadows)', 'realistic']].map(([label, val]) => (
            <button key={val} onClick={() => setStyle(val)} className={`px-3 py-1 rounded ${style === val ? 'btn-primary' : 'btn-secondary'}`}>{label}</button>
          ))}
        </div>
        <p className="text-gray-400 text-xs mt-1">{matcap ? 'ZBrush matcap look (flat + AO).' : 'Lit matte-bone with real directional cast shadows (place the light below).'}</p>
      </div>
      {matcap && (
        <div>
          <div className="flex justify-between text-xs text-gray-500 mb-0.5"><span>Matcap brightness</span><span>{matcapBrightness.toFixed(2)}×</span></div>
          {/* 0.25-2.0, opening at the house 1.1. Drives toneMappingExposure under
              LinearToneMapping (a pure linear multiply). MEASURED on probe.stl, bone median sRGB:
                0.25 -> 96,88,70   0.50 -> 133,123,98   0.80 -> 165,152,122   1.10 -> 190,176,141
              The ceiling is 2.0 because above ~1.5 the highlights blow out and the sutures and
              orbits stop reading — a preview at 2.0 came back flat and washed out. */}
          <input type="range" min={MATCAP_BRIGHTNESS.min} max={MATCAP_BRIGHTNESS.max} step={MATCAP_BRIGHTNESS.step}
            value={matcapBrightness} onChange={(e) => setMatcapBrightness(parseFloat(e.target.value))} className="w-full accent-brand-500" />
        </div>
      )}
      {matcap && (
        <div>
          <div className="flex justify-between text-xs text-gray-500 mb-0.5">
            <span>Stamp relief</span><span>{stampRelief === 0 ? 'off' : `${stampRelief.toFixed(2)}×`}</span>
          </div>
          {/* Matcap only: the lit paths already shade relief correctly, which is why the
              renderer treats reliefShadow as a matcap-path option. */}
          <input type="range" min={STAMP_RELIEF.min} max={STAMP_RELIEF.max} step={STAMP_RELIEF.step}
            value={stampRelief} onChange={(e) => setStampRelief(parseFloat(e.target.value))}
            className="w-full accent-brand-500" />
          <p className="text-gray-400 text-xs mt-1">
            Darkens self-shadowed areas so an engraved case ID reads face-on. Off by default — it
            darkens bone too, and the Surgical Suite Engine does not do it.
          </p>
        </div>
      )}
      {!matcap && (
        <div className="space-y-2">
          <div><div className="flex justify-between text-xs text-gray-500 mb-0.5"><span>Light azimuth</span><span>{lightAz}°</span></div>
            <input type="range" min={-90} max={90} step={5} value={lightAz} onChange={(e) => setLightAz(parseInt(e.target.value))} className="w-full accent-brand-500" /></div>
          <div><div className="flex justify-between text-xs text-gray-500 mb-0.5"><span>Light elevation</span><span>{lightEl}°</span></div>
            <input type="range" min={0} max={80} step={5} value={lightEl} onChange={(e) => setLightEl(parseInt(e.target.value))} className="w-full accent-brand-500" /></div>
          <div><div className="flex justify-between text-xs text-gray-500 mb-0.5"><span>Light intensity</span><span>{lightIntensity.toFixed(1)}×</span></div>
            <input type="range" min={0.3} max={2.5} step={0.1} value={lightIntensity} onChange={(e) => setLightIntensity(parseFloat(e.target.value))} className="w-full accent-brand-500" /></div>
        </div>
      )}
      <label className="flex items-center gap-2 text-sm text-gray-600 border-t border-gray-100 pt-2">
        <input type="checkbox" checked={shadows} onChange={(e) => setShadows(e.target.checked)} /> Shadows <span className="text-gray-400 text-xs">(ambient occlusion — crevice depth)</span>
      </label>
      {shadows && (
        <div>
          <div className="flex justify-between text-xs text-gray-500 mb-0.5"><span>Shadow strength</span><span>{aoIntensity.toFixed(2)}×</span></div>
          {/* 0–4, opening at the house 1.3. The old slider topped out AT 2.00, so the
              range is what widened here — the team now has room above what they use.
              0 = no crevice occlusion at all (GTAO blendIntensity 0), which is a
              legitimate look for a smooth printed part, not a broken one.
              This one is LOCAL, so judge it by crevices and not by an average: 1.30 vs
              2.00 moves the mean only 0.04–1.25 depending on the mesh, but the worst
              pixel by 86–127 levels. */}
          <input type="range" min={AO_INTENSITY.min} max={AO_INTENSITY.max} step={AO_INTENSITY.step}
            value={aoIntensity} onChange={(e) => setAoIntensity(parseFloat(e.target.value))} className="w-full accent-brand-500" />
        </div>
      )}
      <label className="flex items-center gap-2 text-sm text-gray-600">
        <input type="checkbox" checked={flipAP} onChange={(e) => setFlipAP(e.target.checked)} /> Flip front/back (if Anterior &amp; Posterior look swapped)
      </label>
    </div>
  )
}
